<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";

import { platformGet, platformPatch } from "@/common/api/rest/platform";

import type { PlatformType } from "@/common/types/platform";

import FormPlatform from "@/components/forms/form-platform.vue";
import ListTranslate from "@/components/lists/list-translate.vue";
import ListSlug from "@/components/lists/list-slug.vue";
import { breadcrumbSimple, iconMenu, svgIcon } from "@/common/components";

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const platformData = ref<PlatformType | null>(null);

const loading = ref(true);

const platformId = computed(() => route.params.platform as string);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "platform",
    label: t("common.labels.platforms"),
    url: `/register/platform`,
  },
  {
    key: "platform",
    label: platformData.value?.name ?? "...",
    url: `/register/platform/${platformId.value}/edit`,
  },
  {
    key: "edit",
    label: t("slug.labels.slugs"),
  },
]);

const menuList = computed(() => [
  {
    key: "platform",
    icon: "home-line",
    label: t("platform.labels.platform"),
    url: `/register/platform/${platformId.value}/edit`,
  },
  {
    key: "slug",
    icon: "no-image",
    label: t("slug.labels.slugs"),
    url: `/register/platform/${platformId.value}/slug`,
  },
]);

function back() {
  router.push("/register/platform");
}

function changeMenu(url: string) {
  router.push(url);
}

// PLATFORM
function fetchPlatform() {
  platformGet(platformId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        platformData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
    });
}

onMounted(() => {
  fetchPlatform();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="edit"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="platform" size="2rem" />
          {{ t("platform.labels.edit_platform") }}
        </div>

        <div class="actions">
          <button class="btn btn-default" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <icon-menu
          :tabs="menuList"
          active="slug"
          size="3rem"
          @selected="changeMenu($event)"
        />

        <list-slug :owner-id="platformId" type="PLATFORM" />
      </div>
    </section>
  </article>
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;
    }
  }
}
</style>
